import { useEffect, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { Play, VolumeX } from "lucide-react";
import { formatarDuracao, type VideoNoCatalogo } from "@/lib/videoDoProduto";

// O vídeo recomeça sozinho no fim, no card e no carrossel (Pedro, 28/10/2026: «vai ficar tocando»). Desligar aqui faz os
// dois pararem no fim.
const REPETIR = true;

// O navegador com economia de dados ligada (Chrome no Android): não toca sozinho, fica a capa com o ▶.
function economizaDados(): boolean {
  try {
    return !!(navigator as Navigator & { connection?: { saveData?: boolean } }).connection?.saveData;
  } catch {
    return false;
  }
}

// O vídeo do produto no carrossel das fotos. Quando o slide dele fica ativo, toca sozinho e sem som (Pedro, 28/10/2026:
// «Coloca o auto play no vídeo também»): é o único jeito que o navegador deixa, e no iPhone ainda precisa do playsInline.
// O endereço do vídeo só entra na página nessa hora (com o <source> já na página, o WebKit pedia o arquivo mesmo com
// preload="none"): o vídeo que está depois das fotos só baixa quando o cliente passa até ele. Um toque liga o som e
// mostra os controles. Se o navegador recusar o play() sozinho (iPhone em Modo de Pouca Energia, economia de dados), fica
// a capa com o ▶, e o toque toca com som, no próprio gesto. Inteiro na tela (contain): o convite em pé não pode sair
// cortado. O WebM vem primeiro; o iPhone que não toca WebM pega o MP4.
export function VideoNoCarrossel({ video, ativo, alt, onTocando }: { video: VideoNoCatalogo; ativo: boolean; alt: string; onTocando?: (tocando: boolean) => void }) {
  const ref = useRef<HTMLVideoElement>(null);
  // capa: só a capa e o ▶ · sozinho: tocando sem som, sem controles · som: o cliente tocou, com som e controles.
  const [modo, setModo] = useState<"capa" | "sozinho" | "som">("capa");
  const [fontes, setFontes] = useState(false);
  const [tocando, setTocando] = useState(false);

  // As fontes entraram para tocar sozinho (e não pelo toque): carrega e toca quando elas estiverem na página.
  const sozinhoPendente = useRef(false);

  const tocaSemSom = () => {
    const v = ref.current;
    if (!v) return;
    v.muted = true;
    v.defaultMuted = true;
    // O cliente pode ter tocado (com som) enquanto o play() sem som esperava: aí fica o com som.
    void v.play().then(() => setModo((m) => (m === "capa" ? "sozinho" : m)), () => { /* recusou: fica a capa com o ▶ */ });
  };

  // Slide ativo: toca sozinho e sem som. Passou para outra foto (ou fechou): para.
  useEffect(() => {
    if (!ativo) { ref.current?.pause(); return; }
    if (modo === "som" || (modo === "capa" && economizaDados())) return;
    if (fontes) tocaSemSom();
    else { sozinhoPendente.current = true; setFontes(true); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ativo]);

  useEffect(() => {
    if (!fontes || !sozinhoPendente.current) return;
    sozinhoPendente.current = false;
    ref.current?.load();
    tocaSemSom();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fontes]);

  // Os pontinhos do carrossel saem só com os controles na tela: ficavam por cima da barra do vídeo.
  useEffect(() => { onTocando?.(modo === "som" && tocando); }, [modo, tocando, onTocando]);

  const comSom = () => {
    const v = ref.current;
    if (!v) return;
    // flushSync: as fontes entram na página ainda dentro do toque, e o play() sai no mesmo gesto.
    if (!fontes) { sozinhoPendente.current = false; flushSync(() => setFontes(true)); v.load(); }
    flushSync(() => setModo("som"));
    v.muted = false;
    if (v.ended) v.currentTime = 0;
    void v.play().catch(() => { /* o navegador recusou: os controles ficam na tela */ });
  };

  return (
    <div className="relative w-full h-full bg-black" data-testid="video-produto" data-modo={modo}>
      <video
        ref={ref}
        poster={video.capa}
        preload="none"
        playsInline
        loop={REPETIR}
        controls={modo === "som"}
        aria-label={alt}
        className="w-full h-full object-contain"
        onPlay={() => setTocando(true)}
        onPause={() => setTocando(false)}
        // Acabou sem som: volta o ▶ (o toque toca de novo, do começo e com som).
        onEnded={() => { setTocando(false); setModo((m) => (m === "sozinho" ? "capa" : m)); }}
      >
        {fontes && video.webm && <source src={video.webm} type="video/webm" />}
        {fontes && video.mp4 && <source src={video.mp4} type="video/mp4" />}
      </video>
      {modo === "capa" && (
        <button
          type="button"
          onClick={comSom}
          className="absolute inset-0 flex items-center justify-center"
          aria-label="Tocar o vídeo"
          data-testid="video-tocar"
        >
          <span className="w-16 h-16 rounded-full bg-black/55 backdrop-blur-sm text-white flex items-center justify-center shadow-lg">
            <Play className="w-7 h-7 fill-current ml-1" />
          </span>
          {video.duracao ? (
            <span className="absolute top-3 left-3 text-xs font-semibold text-white bg-black/55 px-2 py-0.5 rounded-full">
              {formatarDuracao(video.duracao)}
            </span>
          ) : null}
        </button>
      )}
      {modo === "sozinho" && (
        // O vídeo inteiro é o botão do som; o 🔇 no canto mostra que dá.
        <button type="button" onClick={comSom} className="absolute inset-0" aria-label="Ligar o som" data-testid="video-som">
          <span className="absolute bottom-3 right-3 w-9 h-9 rounded-full bg-black/55 backdrop-blur-sm text-white flex items-center justify-center shadow">
            <VolumeX className="w-4 h-4" />
          </span>
        </button>
      )}
    </div>
  );
}

// O vídeo no card da lista, quando ele é o 1º da ordem: no lugar da capa, tocando sem som enquanto o card está na tela
// (Pedro, 28/10/2026: «se colocar o produto como primeiro vai ficar tocando o vídeo no cardápio»). O endereço só entra na
// página quando o card aparece, e o vídeo para quando ele sai. Se o navegador recusar tocar sozinho, fica a imagem com o ▶.
// O toque é do card (abre o produto), não do vídeo.
export function VideoNoCard({ video, alt }: { video: VideoNoCatalogo; alt: string }) {
  const ref = useRef<HTMLVideoElement>(null);
  const [fontes, setFontes] = useState(false);
  const [recusou, setRecusou] = useState(false);
  const visivel = useRef(false);
  const comFontes = useRef(false);

  const toca = () => {
    const v = ref.current;
    if (!v || !visivel.current) return;
    v.muted = true;
    v.defaultMuted = true;
    void v.play().then(() => setRecusou(false), () => setRecusou(true));
  };

  useEffect(() => {
    const v = ref.current;
    if (!v) return;
    if (typeof IntersectionObserver === "undefined" || economizaDados()) { setRecusou(true); return; }
    const io = new IntersectionObserver(([e]) => {
      visivel.current = !!e?.isIntersecting;
      if (!visivel.current) { v.pause(); return; }
      if (comFontes.current) toca();
      else setFontes(true);
    }, { threshold: 0.5 });
    io.observe(v);
    return () => io.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!fontes) return;
    comFontes.current = true;
    ref.current?.load();
    toca();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fontes]);

  return (
    <div className="relative w-full h-full" data-testid="card-video" data-recusou={recusou ? "1" : undefined}>
      <video
        ref={ref}
        poster={video.capa}
        preload="none"
        muted
        playsInline
        loop={REPETIR}
        aria-label={alt}
        className="w-full h-full object-cover"
      >
        {fontes && video.webm && <source src={video.webm} type="video/webm" />}
        {fontes && video.mp4 && <source src={video.mp4} type="video/mp4" />}
      </video>
      {recusou && (
        <span className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <span className="w-10 h-10 rounded-full bg-black/55 backdrop-blur-sm text-white flex items-center justify-center shadow">
            <Play className="w-4 h-4 fill-current ml-0.5" />
          </span>
        </span>
      )}
    </div>
  );
}
